import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Bell, Check, X as XIcon, UserPlus, UserMinus, Clock } from "lucide-react";
import { toast } from "sonner";

function fmt(dt) {
  if (!dt) return "—";
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

function RequestCard({ req, onAction }) {
  const [notes, setNotes] = useState("");
  const isAdd = req.action === "add";
  const Icon = isAdd ? UserPlus : UserMinus;
  const decided = req.status !== "pending";

  return (
    <Card className={`p-5 ${decided ? "opacity-70" : ""}`} data-testid={`change-request-${req.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className={`h-10 w-10 rounded-md flex items-center justify-center ${isAdd ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-display font-bold">
              {isAdd ? "Add" : "Remove"} household member — {req.member?.name}
            </p>
            <Badge variant={req.status === "pending" ? "secondary" : req.status === "approved" ? "default" : "destructive"} data-testid={`status-${req.id}`}>
              {req.status}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground">From contractor: <strong>{req.contractor_name}</strong></p>
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
            <Clock className="w-3 h-3" /> Submitted {fmt(req.created_at)}
          </p>
        </div>
      </div>

      <div className="bg-secondary/50 rounded-md p-3 text-sm space-y-0.5 mb-3">
        <p><strong>Name:</strong> {req.member?.name}</p>
        {req.member?.relation && <p><strong>Relation:</strong> {req.member.relation}</p>}
        {req.member?.dob && <p><strong>DOB:</strong> {req.member.dob}</p>}
        {req.reason && <p className="pt-1 border-t border-border mt-1"><strong>Reason:</strong> {req.reason}</p>}
      </div>

      {decided ? (
        <div className="text-xs text-muted-foreground">
          {req.status === "approved" ? "Approved" : "Denied"} by <strong>{req.decided_by}</strong> · {fmt(req.decided_at)}
          {req.decision_notes && <p className="mt-1 italic">Notes: {req.decision_notes}</p>}
        </div>
      ) : (
        <>
          <Textarea
            rows={2}
            placeholder="Optional decision notes…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            data-testid={`decision-notes-${req.id}`}
            className="mb-2"
          />
          <div className="flex gap-2">
            <Button onClick={() => onAction(req.id, "approve", notes)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${req.id}`}>
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button onClick={() => onAction(req.id, "deny", notes)} variant="destructive" data-testid={`deny-${req.id}`}>
              <XIcon className="w-4 h-4 mr-1" /> Deny
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default function AdminChangeRequests() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);

  const load = async () => {
    const { data } = await api.get(`/household-change-requests?status=${tab}`);
    setRows(data);
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handle = async (id, action, notes) => {
    try {
      await api.post(`/household-change-requests/${id}/${action}`, { decision_notes: notes });
      toast.success(`Request ${action === "approve" ? "approved" : "denied"}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-change-requests">
      <div>
        <p className="text-label">Approval Queue</p>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <Bell className="w-6 h-6 text-primary" /> Household Change Requests
        </h1>
        <p className="text-sm text-muted-foreground">
          Review contractor-submitted requests to add or remove household members. Approving a request
          updates the contractor's live profile; denying preserves the original roster.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="denied" data-testid="tab-denied">Denied</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="space-y-3 mt-4">
          {rows.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="empty-state">
              {tab === "pending" ? "No pending change requests." : `No ${tab} requests yet.`}
            </Card>
          )}
          {rows.map((r) => <RequestCard key={r.id} req={r} onAction={handle} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
